import { Injectable, inject } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { take, tap, catchError, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { Profile, ProfileState, AccountStatus, AccountStatusType } from '../Models/Profile.model';
import { ApiService } from '../../Api/Service/api.service';
import { RolesService } from '../../Roles/Service/roles.service';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ProfileService {
  private profileStateSubject = new BehaviorSubject<ProfileState>({
    profile: null,
    isLoading: false,
    error: null
  });
  
  public profileState$: Observable<ProfileState> = this.profileStateSubject.asObservable();
  
  private router = inject(Router);

  constructor(
    private apiService: ApiService,
    private rolesService: RolesService,
    private cookieService: CookieService
  ) {}

  /**
   * Set loading state (used by APP_INITIALIZER)
   */
  public setLoadingState(isLoading: boolean): void {
    this.updateState({ isLoading });
  }

  public fetchProfile(userId: string): Observable<Profile | null> {
    this.updateState({ isLoading: true, error: null });
    
    const profileUrl = `${environment.trogonApi}/v1/admin/user/profile?id=${userId}`;
    
    return this.apiService.get(profileUrl, {}).pipe(
      take(1),
      tap((profile: Profile) => {
        this.setProfile(profile);
      }),
      catchError((error) => {
        this.updateState({ 
          isLoading: false, 
          error: 'Failed to fetch profile',
          profile: null 
        });
        return of(null);
      })
    );
  }

  public setProfile(profile: Profile): void {
    if (profile.access && Array.isArray(profile.access) && profile.access.length > 0) {
      this.rolesService.setRoles(profile.access);
    } else {
      this.rolesService.setRoles([]);
    }
    
    this.updateState({
      profile,
      isLoading: false,
      error: null
    });
  }

  public getProfile(): Observable<Profile | null> {
    return new Observable(observer => {
      const currentState = this.profileStateSubject.value;
      observer.next(currentState.profile);
      observer.complete();
    });
  }

  public getProfileState(): Observable<ProfileState> {
    return this.profileState$;
  }

  public clearProfile(): void {
    this.rolesService.clearRoles();
    this.updateState({
      profile: null,
      isLoading: false,
      error: null
    });
  }

  public hasProfile(): boolean {
    return !!this.profileStateSubject.value.profile;
  }

  public getDisplayName(): string {
    const profile = this.profileStateSubject.value.profile;
    if (profile && profile.firstName && profile.lastName) {
      return `${profile.firstName} ${profile.lastName}`;
    }
    return profile?.email || 'Unknown User';
  }

  public isTrialUser(): boolean {
    const profile = this.profileStateSubject.value.profile;
    return profile?.status === AccountStatus.TRIAL;
  }

  public getAccountStatus(): AccountStatusType | 'unknown' {
    const profile = this.profileStateSubject.value.profile;
    return profile?.status || 'unknown';
  }

  public isAccountActive(): boolean {
    const profile = this.profileStateSubject.value.profile;
    return profile?.status === AccountStatus.ACTIVE;
  }

  public isAccountExpired(): boolean {
    const profile = this.profileStateSubject.value.profile;
    return profile?.status === AccountStatus.EXPIRED || 
           (profile?.daysRemaining !== undefined && profile.daysRemaining <= 0);
  }

  public getDaysRemaining(): number | null {
    const profile = this.profileStateSubject.value.profile;
    return profile?.daysRemaining ?? null;
  }

  public isFederatedUser(): boolean {
    const profile = this.profileStateSubject.value.profile;
    return !!profile?.federatedStudentMember;
  }

  public canCreateEvents(): boolean {
    const profile = this.profileStateSubject.value.profile;
    return profile?.eventSettings?.enableCreation ?? false;
  }

  public getUserId(): string | null {
    const profile = this.profileStateSubject.value.profile;
    return profile?.userId || profile?.userid || null;
  }

  public getInstitutionInfo(): { id: string | null; name: string | null } {
    const profile = this.profileStateSubject.value.profile;
    return {
      id: profile?.federatedInstitutionId || null,
      name: profile?.federatedInstitutionName || null
    };
  }
  private updateState(partialState: Partial<ProfileState>): void {
    const currentState = this.profileStateSubject.value;
    this.profileStateSubject.next({
      ...currentState,
      ...partialState
    });
  }

}
